import { defineVariants } from "@/runtime/style-engine/define-variants";

/**
 * Tailwind-like class variants for the `<e-radio>` root container.
 */
export const radioRootClasses = defineVariants({
  base: `
    e-radio
    inline-grid grid-cols-[auto_1fr] gap-x-2 gap-y-1
    min-w-0
  `,

  variants: {
    disabled: {
      true: `opacity-50 cursor-not-allowed [&_*]:cursor-not-allowed!`,
      false: "cursor-pointer",
    },
    encased: {
      true: "grid w-full p-3 cursor-pointer",
      false: "",
    },
  },

  defaults: {
    disabled: false,
    encased: false,
  },
});

/**
 * Tailwind-like class variants for the radio button (button element).
 * Always uses outline style - variant only affects the encasing card.
 */
export const radioButtonClasses = defineVariants({
  base: `
    e-radio-button
    rounded-full
    cursor-pointer
    transition-colors
    shrink-0
    text-(--radio-color)
    outline-2
    outline-offset-1
    outline-(--radio-color)
    focus-visible:outline-(--color-foreground)
  `,

  variants: {
    color: {
      primary: `[--radio-color:var(--color-primary)]`,
      secondary: `[--radio-color:var(--color-secondary)]`,
      accent: `[--radio-color:var(--color-accent)]`,
      error: `[--radio-color:var(--color-error)]`,
      warning: `[--radio-color:var(--color-warning)]`,
      info: `[--radio-color:var(--color-info)]`,
      success: `[--radio-color:var(--color-success)]`,
      neutral: `[--radio-color:var(--color-neutral)]`,
      mono: `[--radio-color:var(--color-foreground)] focus-visible:outline-(--color-background)`,
      inherit: `[--radio-color:currentColor]`,
      background: `[--radio-color:var(--color-background)]`,
    },

    size: {
      xs: `size-2 text-[10px] mx-1 my-2`,
      sm: `size-3 text-[12px] mx-1 my-1.5`,
      md: `size-4 text-[14px] m-1`,
      lg: `size-5 text-[18px] m-1`,
      xl: `size-6 text-[22px] m-1`,
    },

    disabled: {
      true: `opacity-50 cursor-not-allowed!`,
      false: "",
    },

    checked: {
      true: "text-(--radio-color)",
      false: "bg-transparent",
    },
  },

  defaults: {
    color: "neutral",
    size: "md",
    disabled: false,
    checked: false,
  },
});

/**
 * Classes for the radio label wrapper.
 */
export const radioLabelClasses = `
  e-radio-label
  flex-1
  select-none
  flex
  flex-col
  gap-1
`;

/**
 * Classes for the radio label text.
 */
export const radioLabelTextClasses = `
  e-radio-label-text
  col-start-2
  text-sm
  leading-none
  peer-disabled:cursor-not-allowed
  peer-disabled:opacity-70
`;

/**
 * Classes for the radio description text.
 */
export const radioDescriptionClasses = `
  e-radio-description
  col-start-2
  text-xs
  text-(--color-elevation-600)
`;

/**
 * Classes for the radio indicator wrapper.
 */
export const radioIndicatorWrapperClasses = `
  flex items-center justify-center
  w-full h-full
`;

/**
 * Tailwind-like class variants for the radio indicator (icon).
 */
export const radioIndicatorClasses = defineVariants({
  base: ``,

  variants: {
    visible: {
      true: "",
      false: "invisible",
    },
  },

  defaults: {
    visible: "true",
  },
});

/**
 * Classes for the encased radio wrapper (applied to the card element).
 */
export const radioEncasedWrapperClasses = `
  p-0!
  [&_div]:focus-within:ring
  [&_div]:focus-within:ring-(--color-foreground)
  [&_div]:rounded-lg
  [&_div]:h-full
`;

/**
 * Tailwind-like class variants for `<e-radio-group>`.
 */
export const radioGroupClasses = defineVariants({
  base: `gap-2`,

  variants: {
    disabled: {
      true: `opacity-50`,
      false: ``,
    },
    orientation: {
      horizontal: `flex flex-row flex-wrap`,
      vertical: `flex flex-col`,
    },
  },

  defaults: {
    disabled: false,
    orientation: "vertical",
  },
});
